import { CandidateRow } from '@shared/models/element-import.model';
import { ElementQuantityExpressionName } from '@shared/models/element-quantities.model';

/**
 * Which way an area quantity faces. `m²` alone cannot tell a floor from a wall,
 * and the mapped product's category often carries no orientation at all (floor
 * tiles resolve to Ceramics, whose default is `mass`), so the CSV text is the
 * last signal available before falling back to a blind guess.
 */
export type AreaOrientation = Extract<
  ElementQuantityExpressionName,
  'area_top' | 'area_side'
>;

/**
 * Horizontal surfaces, measured from above: floors, slabs, roofs, ceilings.
 * Swedish and English, since imports carry either.
 */
const AREA_TOP_KEYWORDS = [
  'golv',
  'bjälklag',
  'avjämning',
  'klinker',
  'parkett',
  'linoleum',
  'terrass',
  'altan',
  'balkong',
  'tak',
  'floor',
  'slab',
  'deck',
  'roof',
  'ceiling',
  'screed',
  'paving',
] as const;

/** Vertical surfaces, measured from the side: walls, facades, openings. */
const AREA_SIDE_KEYWORDS = [
  'vägg',
  'fasad',
  'dörr',
  'fönster',
  // Swedish definite forms drop the `e`: fönstret, fönstren.
  'fönstr',
  'beklädnad',
  'wall',
  'facade',
  'door',
  'window',
  'partition',
  'cladding',
  'siding',
  'glazing',
] as const;

/**
 * Plain substring matching, no word boundaries: Swedish applies compounding and
 * inflection at the same time ("innerväggar", "undergolven"), so anything
 * stricter loses the common case. False hits are absorbed by the majority vote
 * below rather than prevented.
 *
 * Two keywords are short enough to sit inside an unrelated word often enough to
 * matter. The words hiding them are subtracted again, so "kontakt" nets zero.
 * "takt" alone covers kontakt, takt and taktik.
 */
const KEYWORD_VETOES: Partial<Record<string, readonly string[]>> = {
  tak: ['takt'],
  door: ['outdoor', 'indoor'],
};

const countOccurrences = (haystack: string, needle: string): number => {
  let count = 0;
  let index = haystack.indexOf(needle);
  while (index !== -1) {
    count += 1;
    index = haystack.indexOf(needle, index + needle.length);
  }
  return count;
};

const scoreKeywords = (text: string, keywords: readonly string[]): number =>
  keywords.reduce((score, keyword) => {
    const vetoed = (KEYWORD_VETOES[keyword] ?? []).reduce(
      (total, veto) => total + countOccurrences(text, veto),
      0,
    );
    return score + Math.max(0, countOccurrences(text, keyword) - vetoed);
  }, 0);

/**
 * The row as one lowercase string: its name, its resource code, and every CSV
 * cell. A row rarely names its orientation in the column you would expect, so
 * the whole row is scanned rather than one field.
 */
const rowText = (row: CandidateRow): string => {
  const parts: string[] = [];
  if (row.name) parts.push(row.name);
  if (row.resourceCode) parts.push(row.resourceCode);
  for (const value of Object.values(row.raw)) parts.push(String(value));
  return parts.join(' ').toLowerCase();
};

/**
 * Guess whether a row's area is horizontal or vertical from its text
 * (DEV-3575). A row mentioning both — "klinker på golv och vägg" — goes with
 * whichever side it mentions more often, since this is a guess either way and
 * the majority is the better half of it.
 *
 * Returns `undefined` only when the row is genuinely uninformative: no match at
 * all, or an exact tie. Both leave the choice to the caller's own fallback.
 */
export const resolveAreaOrientationFromRow = (
  row: CandidateRow,
): AreaOrientation | undefined => {
  const text = rowText(row);
  const top = scoreKeywords(text, AREA_TOP_KEYWORDS);
  const side = scoreKeywords(text, AREA_SIDE_KEYWORDS);

  if (top === side) return undefined;
  return top > side ? 'area_top' : 'area_side';
};
